/**
 * A JWT segment is base64**url** (RFC 7519 s.3), which `atob` does not accept:
 * it rejects `-` and `_` outright and wants the `=` padding base64url drops. A
 * perfectly valid token whose payload happens to encode either character would
 * otherwise be read as undecodable, and so as expired -- discarded and refetched
 * on every single read.
 */
function base64UrlToBase64(segment: string) {
  // Regex rather than replaceAll: the consuming apps' lib target predates it.
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');

  return base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
}

/**
 * Decode a JWT's `exp` claim.
 *
 * Returns `null` for anything that is not a JWT carrying a numeric `exp`: a
 * malformed token is not a token with an unknown expiry, it is a token that
 * cannot be used, and callers treat it as expired. Throwing here instead would
 * put the decode failure on whatever happened to call it, several frames away
 * from the bad value.
 */
export function getJwtTokenExpirationDate(token: string): Date | null {
  const tokenPayload = token.split('.')[1];

  if (!tokenPayload) {
    return null;
  }

  try {
    const { exp } = JSON.parse(atob(base64UrlToBase64(tokenPayload)));

    if (typeof exp !== 'number' || !Number.isFinite(exp)) {
      return null;
    }

    const expirationDate = new Date(exp * 1000);

    // A finite `exp` can still fall outside the range a Date can represent,
    // which gives an Invalid Date whose getTime() is NaN. Every comparison
    // against NaN is false, so such a token would read as *not* expired --
    // the opposite of the guard above, reached by a value that looked fine.
    if (Number.isNaN(expirationDate.getTime())) {
      return null;
    }

    return expirationDate;
  } catch {
    return null;
  }
}

/**
 * Whether a token is expired, or will be within `marginSeconds`.
 *
 * The margin is the point of this helper. A token is checked to decide whether
 * it can be *reused*, and one that is valid for another second passes a bare
 * `now >= exp` check and then dies in the consumer's hands. It also gives some
 * tolerance for a client clock running ahead of the issuer's.
 *
 * The default of 0 is the bare check, so adding a margin is always a decision
 * made at the call site rather than one inherited silently.
 */
export function isJwtTokenExpired(token: string, marginSeconds = 0) {
  const expirationDate = getJwtTokenExpirationDate(token);

  if (!expirationDate) {
    return true;
  }

  return Date.now() + marginSeconds * 1000 >= expirationDate.getTime();
}
